import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Building2, Gift, Hourglass, IndianRupee, Plus, Search, WifiOff } from 'lucide-react';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';
import CafeTable from '../components/CafeTable';
import { TRIAL_SOON_DAYS, trialDaysLeft } from '../utils/billingStatus';

// One predicate per quick-stat button; the counts and the table filter share
// them so a button can never show a number its filter doesn't produce.
//
// Billing, not licences (2026-10-16: the licence-based "Active" and "At Risk"
// did not match reality). The same rule as the café page's badge and Billing
// & Revenue: paying once the café's fee date has arrived (and fees are on),
// free trial before that or with no date set. "Gone quiet" is the dashboard's
// and Teams' rule — no PC for 3+ days, cafés that never started left out — so
// a café has one status everywhere.
const CAFE_FILTERS = {
    paying: c => c.fee_active === true,
    trial: c => !c.fee_active,
    trialSoon: c => { const d = trialDaysLeft(c); return d != null && d >= 0 && d <= TRIAL_SOON_DAYS; },
    quiet: c => !!c.quiet_since,
};

export default function CafeRegistry() {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = useAuthStore();

    const [cafes, setCafes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const canEdit = hasPermission(PERMISSIONS.EDIT_CAFE_DETAILS) || isSuperAdmin();

    // Fetch cafés from API
    const fetchCafes = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Archived/deleted cafés too — they get their own filter below.
            const response = await api.get('/internal/cafes', { params: { include_archived: true } });
            setCafes(response.data || []);
        } catch (err) {
            console.error('Failed to fetch cafés:', err);
            setError(err.response?.data?.detail || 'Failed to load cafés');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCafes();
    }, []);

    // Archived and deleted cafés are out of the business: they only appear
    // under the "Archived" filter and never in the counts.
    const liveCafes = useMemo(() => cafes.filter(c => !c.status || c.status === 'active'), [cafes]);
    const closedCafes = useMemo(() => cafes.filter(c => c.status && c.status !== 'active'), [cafes]);

    const filteredCafes = useMemo(() => {
        let result = statusFilter === 'archived' ? [...closedCafes] : [...liveCafes];
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(c =>
                c.name?.toLowerCase().includes(q) ||
                c.owner_name?.toLowerCase().includes(q) ||
                c.owner_email?.toLowerCase().includes(q) ||
                c.location?.toLowerCase().includes(q)
            );
        }
        if (statusFilter !== 'all' && statusFilter !== 'archived') {
            result = result.filter(CAFE_FILTERS[statusFilter]);
        }
        return result;
    }, [liveCafes, closedCafes, searchQuery, statusFilter]);

    const stats = useMemo(() => ({
        total: liveCafes.length,
        ...Object.fromEntries(Object.entries(CAFE_FILTERS).map(([key, test]) => [key, liveCafes.filter(test).length])),
        archived: closedCafes.length,
    }), [liveCafes, closedCafes]);

    // Click a chip to filter, click it again for all cafés.
    const pick = (key) => setStatusFilter(statusFilter === key && key !== 'all' ? 'all' : key);
    const chips = [
        { key: 'all', label: 'All cafés', n: stats.total, icon: Building2, tone: 'neutral' },
        { key: 'paying', label: 'Paying', n: stats.paying, groupStart: true, icon: IndianRupee, tone: 'success' },
        { key: 'trial', label: 'Free trial', n: stats.trial, icon: Gift, tone: 'info' },
        { key: 'trialSoon', label: 'Trial ends this week', n: stats.trialSoon, warn: true, icon: Hourglass, tone: 'warning', title: 'The platform fee starts within 7 days' },
        { key: 'quiet', label: 'Gone quiet', n: stats.quiet, alarm: true, groupStart: true, icon: WifiOff, tone: 'danger', title: 'No PC has connected for 3+ days (the same rule as the dashboard and Teams)' },
        { key: 'archived', label: 'Archived', n: stats.archived, end: true, icon: Archive, tone: 'neutral' },
    ];

    return (
        <div className="cafe-registry">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Cafés</h1>
                    <p className="page-subtitle">{stats.total} café{stats.total === 1 ? '' : 's'} • {stats.paying} paying • {stats.trial} on free trial</p>
                </div>
                <button
                    className="btn btn--primary"
                    onClick={() => navigate('/cafes/new')}
                    disabled={!canEdit}
                    title={!canEdit ? 'Read-only access' : undefined}
                >
                    <Plus size={16} />
                    Add Café
                </button>
            </div>

            {/* Quick stats: the café page's slim chips, each a filter, grouped by
                meaning — billing, then PCs, and Archived (not a live café) at the end. */}
            <div className="quick-stats">
                {chips.map((chip) => {
                    const on = statusFilter === chip.key;
                    return (
                        <button
                            key={chip.key}
                            type="button"
                            className={`quick-stat${on ? ' quick-stat--active' : ''}${chip.groupStart ? ' quick-stat--group' : ''}${chip.end ? ' quick-stat--end' : ''} is-${chip.tone}${chip.n === 0 ? ' is-empty' : ''}${chip.n > 0 && (chip.warn || chip.alarm) ? ' is-flag' : ''}`}
                            aria-pressed={on}
                            onClick={() => pick(chip.key)}
                            title={chip.title}
                        >
                            <chip.icon size={15} className="quick-stat__icon" aria-hidden="true" />
                            <span className={`quick-stat__value${chip.n > 0 && chip.warn ? ' is-warn' : ''}${chip.n > 0 && chip.alarm ? ' is-alarm' : ''}`}>{chip.n}</span>
                            <span className="quick-stat__label">{chip.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="search-bar">
                <Search size={16} className="search-bar__icon" />
                <input
                    type="text"
                    placeholder="Search cafés by name, owner, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-bar__input"
                />
            </div>

            {/* Table */}
            <div className="card table-card">
                <CafeTable
                    cafes={filteredCafes}
                    isLoading={isLoading}
                    error={error}
                    onRetry={fetchCafes}
                    maxHeight={420}
                    showCreated
                />
            </div>

            <style>{`
        .cafe-registry {
          /* noLag rebrand: scoped to this page only, same pattern as the
             other rebranded pages. Flows into .btn--primary (Add Café),
             .quick-stat--active, .search-bar__input:focus, and CafeTable's
             café-avatar icon automatically — none of them are status
             colors (CAFE_STATUS_CONFIG's badge--success/info/warning/
             danger/neutral is a separate token, untouched). */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1200px;
        }
        .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-6); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }

        .quick-stats { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-bottom: var(--space-5); }
        /* Slim chips, count and label on one line — the café page's cards. */
        .quick-stat {
          display: inline-flex; align-items: baseline; gap: var(--space-2); padding: 6px 12px; cursor: pointer;
          color: var(--text-primary); background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.16); border-radius: var(--radius-md);
          transition: all var(--duration-fast) var(--ease-out);
        }
        .quick-stat:hover { border-color: rgba(255, 255, 255, 0.32); }
        .quick-stat--group { margin-left: var(--space-4); }
        /* Each chip's icon in the colour of what it means. */
        .quick-stat { align-items: center; }
        .quick-stat__icon { flex-shrink: 0; color: var(--text-tertiary); }
        .quick-stat.is-success .quick-stat__icon { color: var(--status-success); }
        .quick-stat.is-info .quick-stat__icon { color: var(--status-info); }
        .quick-stat.is-warning .quick-stat__icon { color: var(--status-warning); }
        .quick-stat.is-danger .quick-stat__icon { color: var(--status-danger); }
        /* Nothing in it: faded, so the eye goes to the chips that have something. */
        .quick-stat.is-empty:not(.quick-stat--active) { opacity: 0.5; }
        /* A warning chip with something in it gets a faint wash of its colour. */
        .quick-stat.is-warning.is-flag { background: rgba(245, 165, 36, 0.10); border-color: rgba(245, 165, 36, 0.45); }
        .quick-stat.is-danger.is-flag { background: rgba(240, 75, 75, 0.10); border-color: rgba(240, 75, 75, 0.45); }
        /* The chip you are on, in its own colour. */
        .quick-stat--active { box-shadow: 0 0 0 1px var(--accent-primary); }
        .quick-stat--active.is-success { border-color: var(--status-success); background: rgba(34, 197, 94, 0.16); box-shadow: 0 0 0 1px var(--status-success); }
        .quick-stat--active.is-info { border-color: var(--status-info); background: rgba(59, 130, 246, 0.18); box-shadow: 0 0 0 1px var(--status-info); }
        .quick-stat--active.is-warning { border-color: var(--status-warning); background: rgba(245, 165, 36, 0.22); box-shadow: 0 0 0 1px var(--status-warning); }
        .quick-stat--active.is-danger { border-color: var(--status-danger); background: rgba(240, 75, 75, 0.22); box-shadow: 0 0 0 1px var(--status-danger); }
        .quick-stat--end { margin-left: auto; }
        .quick-stat--active { border-color: var(--accent-primary); background: var(--accent-primary-subtle); }
        .quick-stat__value { font-size: var(--text-lg); font-weight: 600; }
        .quick-stat__value.is-warn { color: var(--status-warning); }
        .quick-stat__value.is-alarm { color: var(--status-danger); }
        .quick-stat__label { font-size: var(--text-sm); color: var(--text-secondary); white-space: nowrap; }
        .search-bar {
          position: relative;
          margin-bottom: var(--space-6);
        }
        .search-bar__icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-tertiary);
        }
        .search-bar__input {
          width: 100%;
          padding: 14px 16px 14px 44px;
          font-size: var(--text-base);
          background: var(--glass-bg);
          backdrop-filter: blur(var(--glass-blur));
          border: 1px solid var(--glass-border);
          border-radius: var(--radius-lg);
          color: var(--text-primary);
          transition: all var(--duration-fast) var(--ease-out);
        }
        .search-bar__input::placeholder { color: var(--text-quaternary); }
        .search-bar__input:hover { border-color: var(--glass-border-hover); }
        .search-bar__input:focus { outline: none; border-color: var(--accent-primary); box-shadow: var(--shadow-focus); }

        .table-card { padding: 0; overflow: hidden; }
      `}</style>
        </div>
    );
}
